import React, { useState, useMemo } from "react";
import { X, FileText, CheckSquare, Square } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Column-picker modal for PDF/Excel exports.
 * Lets the admin choose which columns to include before downloading.
 * Selection is persisted in localStorage under `storageKey` so it sticks across sessions.
 *
 * Props:
 *   open: bool — modal visibility
 *   onClose: () => void
 *   columns: [{ key, header }] — the full list available
 *   storageKey: string — localStorage key for the saved selection (e.g. "pdf-cols-bilhetes")
 *   title: string — modal title (e.g. "Exportar PDF · Gestão de Bilhetes")
 *   accentColor: string — hex for primary button
 *   onConfirm: (selectedColumns) => void — called with the filtered columns array
 */
export function ColumnPickerModal({
  open,
  onClose,
  columns,
  storageKey,
  title = "Escolher colunas a exportar",
  accentColor = "#f59e0b",
  onConfirm,
  confirmLabel = "Exportar",
}) {
  const initial = useMemo(() => {
    if (!storageKey) return columns.map((c) => c.key);
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved)) return saved.filter((k) => columns.some((c) => c.key === k));
      }
    } catch (_) {}
    return columns.map((c) => c.key);
  }, [columns, storageKey]);

  const [selected, setSelected] = useState(new Set(initial));

  if (!open) return null;

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set(columns.map((c) => c.key)));
  const selectNone = () => setSelected(new Set());

  const confirm = () => {
    const filtered = columns.filter((c) => selected.has(c.key));
    if (filtered.length === 0) return;
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify(filtered.map((c) => c.key))); } catch (_) {}
    }
    onConfirm(filtered);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.85)" }}
      data-testid="column-picker-modal"
    >
      <div
        className="bg-zinc-900 border border-white/10 rounded-2xl max-w-md w-full max-h-[85vh] flex flex-col"
        style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.6)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5" style={{ color: accentColor }} />
            <h3 className="text-white font-semibold">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white transition-colors"
            data-testid="column-picker-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Quick actions */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-zinc-950/50">
          <span className="text-xs text-zinc-500">
            {selected.size} de {columns.length} seleccionadas
          </span>
          <div className="flex gap-2">
            <button
              onClick={selectAll}
              className="text-xs text-zinc-300 hover:text-white underline"
              data-testid="column-picker-select-all"
            >
              Todas
            </button>
            <span className="text-zinc-700">·</span>
            <button
              onClick={selectNone}
              className="text-xs text-zinc-300 hover:text-white underline"
              data-testid="column-picker-select-none"
            >
              Nenhuma
            </button>
          </div>
        </div>

        {/* Column list */}
        <div className="flex-1 overflow-y-auto p-3">
          {columns.map((col) => {
            const checked = selected.has(col.key);
            return (
              <button
                key={col.key}
                onClick={() => toggle(col.key)}
                className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-colors ${
                  checked ? "bg-white/5" : "hover:bg-white/5"
                }`}
                data-testid={`column-picker-${col.key}`}
              >
                {checked ? (
                  <CheckSquare className="w-4 h-4 shrink-0" style={{ color: accentColor }} />
                ) : (
                  <Square className="w-4 h-4 shrink-0 text-zinc-600" />
                )}
                <span className={`text-sm ${checked ? "text-white" : "text-zinc-400"}`}>
                  {col.header}
                </span>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t border-white/10">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 border-white/10 text-zinc-300"
            data-testid="column-picker-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={confirm}
            disabled={selected.size === 0}
            className="flex-1 text-black font-semibold"
            style={{ backgroundColor: accentColor }}
            data-testid="column-picker-confirm"
          >
            {confirmLabel} ({selected.size})
          </Button>
        </div>
      </div>
    </div>
  );
}

ColumnPickerModal.defaultProps = {};
